import { useAuth } from "react-oidc-context"
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom"
import { useEffect, useMemo, useState } from "react"
import { LoadingComponent } from "@/core/infraestrucutre/components/Loading.component";
import { AppConfigService } from "@kf-react/config";
import { type ApplicationConfig } from "@/core/domain/app.config";
import { useKaimanService } from "@/core/application/services/services.instances";
import { TermsAndConditionsModal } from "@/components/TermsAndConditionsModal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { publicRoutes } from "./public.routes";
import { privateRoutes } from "./private.routes";

function LogoutHandler() {
    const auth = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        auth.signoutRedirect();
        navigate("/login", { replace: true });
    }, [auth, navigate]);
    return <LoadingComponent />;
}

export const MainRouter = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const kaimanService = useKaimanService();

    const legalConfig = useMemo(() => AppConfigService.getInstance<ApplicationConfig>()?.config?.legal, []);
    const profile = auth.user?.profile as { termsAccepted?: unknown; termsAcceptedDate?: string; termsVersion?: string } | undefined;

    const [localTermsAcceptance, setLocalTermsAcceptance] = useState<{ accepted: boolean; version?: string } | null>(null);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [termsCheckboxChecked, setTermsCheckboxChecked] = useState(false);
    const [isSubmittingTerms, setIsSubmittingTerms] = useState(false);

    const parseBoolean = (value: unknown) => value === true || value === "true" || value === "TRUE" || value === 1 || value === "1";
    const termsAcceptedClaim = profile?.termsAccepted;
    const termsVersionClaim = profile?.termsVersion;

    useEffect(() => {
        if (auth.isLoading || !auth.isAuthenticated || !legalConfig?.termsVersion) {
            setIsTermsModalOpen(false);
            return;
        }

        const remoteAccepted = parseBoolean(termsAcceptedClaim);
        const remoteVersionMatches = termsVersionClaim === legalConfig.termsVersion;
        const localAccepted = Boolean(localTermsAcceptance?.accepted && localTermsAcceptance.version === legalConfig.termsVersion);
        const shouldOpen = !(remoteAccepted && remoteVersionMatches) && !localAccepted;

        setIsTermsModalOpen(shouldOpen);
        if (shouldOpen) {
            setTermsCheckboxChecked(false);
        }
    }, [auth.isLoading, auth.isAuthenticated, legalConfig?.termsVersion, termsAcceptedClaim, termsVersionClaim, localTermsAcceptance]);

    const handleAcceptTerms = async () => {
        if (!legalConfig?.termsVersion || !termsCheckboxChecked) {
            return;
        }
        try {
            setIsSubmittingTerms(true);
            await kaimanService.updateUserTermsAcceptance({
                termsAccepted: true,
                termsAcceptedDate: new Date().toISOString(),
                termsVersion: legalConfig.termsVersion,
            });
            setLocalTermsAcceptance({ accepted: true, version: legalConfig.termsVersion });
            setIsTermsModalOpen(false);
            toast.success(t("terms_modal.success_message"));
        } catch (error) {
            console.error("Error accepting terms", error);
            toast.error(t("terms_modal.error_message"));
        } finally {
            setIsSubmittingTerms(false);
        }
    };

    useEffect(() => {
        if (auth.isAuthenticated && (location.pathname === "/welcome" || location.pathname === "/")) {
            navigate("/discoverAgents", { replace: true });
        }
    }, [auth.isAuthenticated, navigate, location.pathname]);

    if (auth.isLoading || auth.activeNavigator === "signinSilent") {
        return <LoadingComponent />;
    }

    return (
        <>
            <TermsAndConditionsModal
                open={isTermsModalOpen}
                title={t("terms_modal.title")}
                content={legalConfig?.termsContent || t("terms_modal.default_content")}
                checkboxLabel={t("terms_modal.accept_checkbox")}
                confirmLabel={t("terms_modal.accept_button")}
                isChecked={termsCheckboxChecked}
                onCheckedChange={(checked) => setTermsCheckboxChecked(Boolean(checked))}
                onConfirm={handleAcceptTerms}
                isSubmitting={isSubmittingTerms}
            />
            <Routes>
                {publicRoutes.map((route) => (
                    <Route key={route.path} path={route.path} element={route.element} />
                ))}
                {privateRoutes.map((route) => (
                    <Route key={route.path} path={route.path} element={route.element} />
                ))}
                <Route path="/logout" element={<LogoutHandler />} />
                <Route path="*" element={<Navigate to={auth.isAuthenticated ? "/discoverAgents" : "/welcome"} replace />} />
            </Routes>
        </>
    )
}